/**
 * RoomSoloPrep (PPL-30 → redesigned PPL-65) — the private 1:1 f8 prep page on web.
 *
 * UX intent: this is the most important moment in the room flow. f8 must
 * feel like the user's coach — theirs alone — before the joint room. The
 * other side cannot see anything that happens here.
 *
 * Per-side calibration (PPL-65):
 *  - inviter (warm) — coral/ember/magenta aurora. f8 picks up "the thing
 *    she's been carrying". No acknowledge-the-accept beat (she initiated).
 *  - invitee (cool) — teal/cyan/indigo aurora. f8 first CLOSES THE LOOP on
 *    the accept ("you're in — that's all they see"), THEN asks how it
 *    landed.
 *
 * Read/write model:
 *  - All state comes from `useRoomPrep`, which subscribes to the caller's
 *    OWN prep thread (`mediations/{id}/prep/{uid}/messages`) and the root
 *    mediation doc, and exposes the `sendMessage` writer.
 *  - The other participant's first name is fetched via the public-profile
 *    callable so the per-side copy can address them by name.
 *
 * Voice: lowercase throughout. No therapy jargon. Text-only in v1 (voice
 * v2 is deferred; voice bypasses the consent chokepoint).
 */

import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import useRoomPrep from '../hooks/useRoomPrep';
import useMediaQuery from '../hooks/useMediaQuery';
import { MESSAGE_LIMIT, generateRoomPrepChips, getMediationCounterpart } from '../services/roomService';
import { pathForState } from '../utils/roomRouting';
import type { RoomMessage } from '../types/room';

const MAX_INPUT_LENGTH = 600;

// ─── Per-side configuration ──────────────────────────────────
// Per the project rule (CLAUDE.md): design-canvas placeholder strings that
// depend on the user / other party / conversation MUST NOT be hardcoded
// here. The suggested-reply chips ("a way in") are generated server-side
// from the live mediation context — see `generateRoomPrepChips` in
// roomService + `generateRoomPrepChipsEurW` in cloud-functions. What
// stays here is layout/aura: colours, the static fallback openings for
// the brief window before f8's first real message arrives, and exit-row
// copy (parameterised on the other person's name).

type SideKey = 'inviter' | 'invitee';

interface SideConfig {
  /** Aurora tint hex stops (a → b → c) + a side-specific dark wash. */
  aurora: { a: string; b: string; c: string; wash: string };
  /** Aurora's primary "deep" colour — used on borders + composer gradient. */
  deep: string;
  /** Aurora's primary "soft" colour — used on icons + chip arrows. */
  soft: string;
  /** Fallback opening lines shown if no f8 message has arrived yet. */
  opening: (otherName: string) => string[];
  /** Quiet exit row above the composer. */
  exit: (otherName: string) => { head: string; sub: string };
  /** Exit button label. */
  exitLabel: string;
}

const PR_SIDE: Record<SideKey, SideConfig> = {
  inviter: {
    aurora: { a: '#fb923c', b: '#f43f5e', c: '#d946ef', wash: 'rgba(54,14,12,0.62)' },
    deep: '#d946ef',
    soft: '#f0abfc',
    opening: (other) => [
      `okay — just us now. nothing you say in here reaches ${other} unless you decide, later, to carry it over.`,
      `you've been holding something. when you picture telling ${other} — what's the part that's hardest to say out loud?`,
    ],
    exit: (other) => ({
      head: 'hold the invitation · not ready for the room',
      sub: `${other} just sees you're still preparing — not why, not anything you've said here.`,
    }),
    exitLabel: 'hold',
  },
  invitee: {
    aurora: { a: '#22d3ee', b: '#38bdf8', c: '#6366f1', wash: 'rgba(8,20,44,0.62)' },
    deep: '#818cf8',
    soft: '#a5b4fc',
    opening: (other) => [
      `you're in. i've let ${other} know you accepted — that's all they see, nothing else. the rest of this is just us.`,
      `their invite landed a few minutes ago. before anything they might've said — how did it actually land for you?`,
    ],
    exit: (other) => ({
      head: 'end here · the prep was enough',
      sub: `${other} only sees that you finished. the joint room won't open unless you choose it.`,
    }),
    exitLabel: 'end prep',
  },
};

// ─── Atoms ───────────────────────────────────────────────────

function F8Dot({ size = 16, ring = false }: { size?: number; ring?: boolean }) {
  const core = (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 32% 30%, #f0abfc, #d946ef 52%, #7c3aed)',
        boxShadow: `0 0 ${size * 0.7}px rgba(217,70,239,0.65)`,
        flexShrink: 0,
      }}
    />
  );
  if (!ring) return core;
  return (
    <span
      style={{
        width: size + 14,
        height: size + 14,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(217,70,239,0.07)',
        border: '1px solid rgba(217,70,239,0.32)',
      }}
    >
      {core}
    </span>
  );
}

function PrepAurora({ tint }: { tint: SideConfig['aurora'] }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-26%',
          left: '-10%',
          width: '66%',
          height: '76%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tint.a} 0%, transparent 64%)`,
          filter: 'blur(85px)',
          opacity: 0.62,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '6%',
          right: '-16%',
          width: '62%',
          height: '78%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tint.b} 0%, transparent 64%)`,
          filter: 'blur(95px)',
          opacity: 0.5,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-34%',
          left: '14%',
          width: '60%',
          height: '68%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tint.c} 0%, transparent 64%)`,
          filter: 'blur(95px)',
          opacity: 0.46,
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(130% 95% at 50% 42%, transparent 0%, ${tint.wash} 88%)`,
        }}
      />
    </div>
  );
}

const STAGE_LABELS = ['invite', 'prep', 'rules', 'framing', 'room'];

function StageDots({ current = 2, compact = false }: { current?: number; compact?: boolean }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {/* On narrow screens drop the dots so the header fits — keep the count. */}
      {!compact &&
        STAGE_LABELS.map((stage, i) => {
          const n = i + 1;
          const cur = n === current;
          const done = n < current;
          return (
            <span
              key={stage}
              style={{
                width: cur ? 16 : 6,
                height: 6,
                borderRadius: 999,
                background: cur
                  ? 'linear-gradient(90deg,#f0abfc,#a855f7)'
                  : done
                    ? 'rgba(217,70,239,0.45)'
                    : 'rgba(255,255,255,0.14)',
              }}
            />
          );
        })}
      <span style={{ marginLeft: compact ? 0 : 7, fontSize: 11, fontWeight: 600, color: 'rgba(232,220,250,0.5)' }}>
        {current}/5
      </span>
    </div>
  );
}

function F8Bubble({
  children,
  kicker,
}: {
  children: React.ReactNode;
  kicker?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
      <F8Dot size={16} ring />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: 560,
          padding: '14px 18px',
          borderRadius: 18,
          borderTopLeftRadius: 6,
          background: 'rgba(28,16,38,0.72)',
          border: '1px solid rgba(217,70,239,0.22)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#f0abfc',
            }}
          >
            f8
          </span>
          {kicker && (
            <span style={{ fontSize: 11, color: 'rgba(232,220,250,0.5)', fontStyle: 'italic' }}>
              · {kicker}
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 15,
            lineHeight: 1.55,
            color: 'rgba(245,237,255,0.95)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div
        style={{
          maxWidth: 560,
          padding: '12px 16px',
          borderRadius: 18,
          borderTopRightRadius: 6,
          background: 'linear-gradient(135deg, rgba(217,70,239,0.18), rgba(168,85,247,0.12))',
          border: '1px solid rgba(217,70,239,0.28)',
          fontSize: 15,
          lineHeight: 1.5,
          color: '#fff',
          whiteSpace: 'pre-wrap',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ReplyChip({
  label,
  arrowColor,
  onClick,
  disabled,
}: {
  label: string;
  arrowColor: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        appearance: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        padding: '10px 13px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.045)',
        border: '1px solid rgba(255,255,255,0.11)',
        color: 'rgba(240,232,250,0.92)',
        fontSize: 13.5,
        fontWeight: 500,
        textAlign: 'left',
        width: '100%',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span>{label}</span>
      <span style={{ marginLeft: 'auto', opacity: 0.6 }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 4v6H7m0 0l3-3m-3 3l3 3"
            stroke={arrowColor}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
}

// ─── Page ────────────────────────────────────────────────────

const isF8Message = (m: RoomMessage): boolean =>
  m.author === 'f8' || m.isArtificial === true;

/**
 * variant:
 *  - 'prep'    — the solo prep chat (stage 2). Advancing routes to /consent.
 *  - 'waiting' — the waiting room (you've confirmed your rules and are waiting
 *    for the other side). Same f8 chat / same prep thread, but it auto-promotes
 *    you into the joint chat the moment the room leaves `prep` (i.e. the other
 *    side confirms too). The last person to confirm never lands here — the
 *    consent screen sends them straight to /opener.
 *  - 'aside'   — "step aside" from an ACTIVE room: a private moment with f8 in
 *    the same thread. The only difference from prep is the exit returns you to
 *    the room (NOT forward into the consent flow, which would be nonsense
 *    mid-room) and there's no stage indicator.
 */
const RoomSoloPrep: React.FC<{ variant?: 'prep' | 'waiting' | 'aside' }> = ({ variant = 'prep' }) => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const isWaiting = variant === 'waiting';
  const isAside = variant === 'aside';
  // waiting + aside share the "side excursion" chrome (direct back button, no
  // consent-advance popover); they differ only in where "back" goes.
  const isExcursion = isWaiting || isAside;

  const {
    mediation,
    messages,
    side,
    isSending,
    initialLoadComplete,
    error,
    atLimit,
    sendMessage,
  } = useRoomPrep(mediationId, uid);

  const [inputText, setInputText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const [otherName, setOtherName] = useState<string>('them');
  const [exitOpen, setExitOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 480px)');

  const canSend = inputText.trim().length > 0 && !isSending;

  // Fetch the other participant's first name so per-side copy and the header
  // can address them by name. The room rule blocks reading their `users/` doc
  // directly, and getPublicUserProfileEurW is intentionally handle-only — so
  // use the mediation-scoped counterpart resolver, which returns firstName for
  // either side.
  useEffect(() => {
    if (!mediationId || !uid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMediationCounterpart(mediationId);
        if (cancelled) return;
        const { firstName, tetherHandle } = res.counterpart;
        const name = (firstName || tetherHandle || '').toLowerCase().trim();
        if (name) setOtherName(name);
      } catch {
        // leave fallback
      }
    })();
    return () => { cancelled = true; };
  }, [mediationId, uid]);

  // Auto-scroll on new messages / send / typing transitions.
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Follow the shared state out of `prep`.
  //
  // Waiting room: the moment the room leaves `prep` (the other side has
  // confirmed their rules too), promote this user into the joint flow. The
  // opener handles the brief synthesis-generating window with its own loading
  // state, so routing there as soon as state advances is safe.
  //
  // This used to promote on `state !== 'prep'` straight to the opener, which
  // stranded people: `ended` also satisfies `!== 'prep'`, so a room ended by
  // the other side left the waiting user on the opener's "f8 is preparing the
  // synthesis…" spinner forever. `pathForState` routes each state where it
  // actually belongs.
  //
  // The plain prep variant follows only the terminal case: an `ended` room
  // makes the composer a lie (every send fails server-side). `aside` is
  // excluded — its room is `ongoing` and RoomSession owns that lifecycle.
  useEffect(() => {
    if (isAside || !mediationId || !mediation?.state) return;
    const { state } = mediation;
    if (state === 'prep') return;
    if (!isWaiting && state !== 'ended') return;
    navigate(pathForState(mediationId, state), { replace: true });
  }, [isWaiting, isAside, mediationId, mediation, navigate]);

  const sideKey: SideKey = side === 'invitee' ? 'invitee' : 'inviter';
  const cfg = PR_SIDE[sideKey];
  const fallbackOpening = useMemo(() => cfg.opening(otherName), [cfg, otherName]);
  const exitCopy = useMemo(() => cfg.exit(otherName), [cfg, otherName]);

  // PPL-65 — server-generated "a way in" chips. Replaces the hardcoded
  // canvas strings: chips are calibrated on the live mediation context
  // (reason + side + what f8 just said) by `generateRoomPrepChipsEurW`.
  // We fetch once the prep stream has loaded and the user hasn't sent
  // anything yet — past that point chips disappear anyway (showChips
  // gates on "no user messages").
  const [chips, setChips] = useState<string[]>([]);
  const chipsFetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!mediationId || !side || !initialLoadComplete) return;
    const userHasReplied = messages.some((m) => !isF8Message(m));
    if (userHasReplied) return;
    // Refetch when f8 posts a new opener/reply — chips should riff on it.
    const lastF8Id = messages.filter(isF8Message).slice(-1)[0]?.id ?? 'init';
    const cacheKey = `${mediationId}:${lastF8Id}`;
    if (chipsFetchedFor.current === cacheKey) return;
    chipsFetchedFor.current = cacheKey;
    let cancelled = false;
    (async () => {
      try {
        const res = await generateRoomPrepChips(mediationId);
        if (cancelled) return;
        if (res?.chips && Array.isArray(res.chips) && res.chips.length > 0) {
          setChips(res.chips);
        }
      } catch (err) {
        // Soft fail — chips are tap-to-prefill; the composer remains the
        // primary way in. No toast, no fallback hardcoded list.
        console.warn('generateRoomPrepChips failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [mediationId, side, initialLoadComplete, messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };

  const handleSend = async (text?: string) => {
    // Default to the textarea content when called without an explicit string
    // (Enter-to-send, primary send button). When a chip passes its label in,
    // use that exact value — don't fall back to inputText, which may be empty.
    const candidate = typeof text === 'string' ? text : inputText;
    const trimmed = candidate.trim();
    if (!trimmed) return;
    const prev = inputText;
    setInputText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    try {
      await sendMessage(trimmed);
    } catch {
      // Restore so the user doesn't lose what they typed.
      setInputText(prev);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Primary advance — both sides use this when they're ready to set sharing
  // rules and move toward the joint room. It does NOT end the mediation; it
  // just routes the user to /consent. The server-side prep→rules_set
  // transition happens when consent.signedAt is set for both sides.
  const handleAdvance = () => {
    if (!mediationId) return;
    navigate(`/rooms/${mediationId}/consent`);
  };

  // (Removed handleQuietExit — both "ready" and "end here · the prep was
  // enough" mean the same thing in plain English: I'm done with prep, let's
  // move on. Collapsed into the single handleAdvance above. The original
  // endMediationPrepSoloEurW "I figured it out alone, no joint room" exit is
  // not exposed from prep at all; if it's needed, /end is the place for it.)

  if (!mediationId) {
    return (
      <div style={{ padding: 24, color: 'rgba(232,220,250,0.7)' }}>
        <p>room not found.</p>
      </div>
    );
  }
  if (!uid) {
    return (
      <div style={{ padding: 24, color: 'rgba(232,220,250,0.7)' }}>
        <p>sign in to open this prep.</p>
      </div>
    );
  }

  const isEmpty = initialLoadComplete && messages.length === 0;
  const showChips =
    initialLoadComplete && messages.filter((m) => !isF8Message(m)).length === 0;

  // height:100% (not 100vh) — the app shell already wraps this in <main> which
  // has overflow-y:auto. Using 100vh produces a body scroll because the inner
  // content ends up taller than <main>'s remaining viewport. Filling 100% of
  // the parent + flex:1 messages area keeps all scrolling internal.
  const containerStyle: CSSProperties = {
    position: 'relative',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0510',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflow: 'hidden',
  };

  return (
    <div style={containerStyle}>
      <PrepAurora tint={cfg.aurora} />

      {/* ── ONE compact room strip: back · f8 dot · title · privacy chip · steps · end-prep popover ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 8 : 12,
          padding: isMobile ? '11px 14px' : '11px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(14,8,20,0.35)',
        }}
      >
        {/* No back chevron — the rooms flow is forward-only (PPL-161); each
            variant's own pill on the right (end prep / back to rooms / back to
            room) is the only exit. */}
        <F8Dot size={9} />
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            minWidth: 0,
            flex: 1,
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
            {isWaiting ? 'waiting room' : isAside ? 'stepped aside' : 'prep with f8'}
          </span>
          <span
            style={{
              fontSize: 12,
              color: 'rgba(232,220,250,0.45)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {isWaiting ? `· waiting for ${otherName}` : isAside ? '· private with f8' : `· about ${otherName}`}
          </span>
        </div>

        {/* privacy chip — full text on hover */}
        <span
          title={`private — ${otherName} never sees what's said here unless you choose to carry it over`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 11px 5px 9px',
            borderRadius: 999,
            flexShrink: 0,
            background: `${cfg.deep}1f`,
            border: `1px solid ${cfg.deep}4d`,
            cursor: 'default',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z"
              stroke={cfg.soft}
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: cfg.soft }}>private</span>
        </span>

        {/* compact step indicator — not shown for a mid-room "step aside" */}
        {!isAside && (
          <div style={{ flexShrink: 0 }}>
            <StageDots current={isWaiting ? 3 : 2} compact={isMobile} />
          </div>
        )}

        {/* prep: end-prep button + confirmation popover (advance to /consent).
            waiting: a plain "back to rooms" (auto-promotes when both confirm).
            aside: a plain "back to room" — return to the active conversation. */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() =>
              isAside
                ? navigate(`/rooms/${mediationId}/room`)
                : isWaiting
                  ? navigate('/rooms')
                  : setExitOpen((o) => !o)
            }
            aria-expanded={isExcursion ? undefined : exitOpen}
            style={{
              appearance: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '7px 12px',
              borderRadius: 999,
              background: exitOpen ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.13)',
              color: 'rgba(240,232,250,0.88)',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2M10 12h11m0 0l-3-3m3 3l-3 3"
                stroke="rgba(240,232,250,0.7)"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isAside ? 'back to room' : isWaiting ? 'back to rooms' : 'end prep'}
          </button>
          {!isExcursion && exitOpen && (
            <div
              role="dialog"
              aria-label="end prep"
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: 296,
                zIndex: 20,
                padding: '15px 16px',
                borderRadius: 16,
                background: 'rgba(24,14,32,0.98)',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-0.005em' }}>
                end here · the prep was enough
              </div>
              <div style={{ fontSize: 12, color: 'rgba(232,220,250,0.65)', marginTop: 5, lineHeight: 1.5 }}>
                {otherName} only sees that you finished. next, you&apos;ll set what f8 can carry into the shared room.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
                <button
                  type="button"
                  onClick={() => setExitOpen(false)}
                  style={{
                    appearance: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    flex: 1,
                    padding: '9px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(240,232,250,0.85)',
                    fontSize: 12.5,
                    fontWeight: 600,
                  }}
                >
                  keep going
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setExitOpen(false);
                    handleAdvance();
                  }}
                  style={{
                    appearance: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    flex: 1,
                    padding: '9px',
                    borderRadius: 10,
                    background: `linear-gradient(135deg, ${cfg.deep}, #a855f7)`,
                    border: 0,
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 700,
                  }}
                >
                  next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            position: 'relative',
            zIndex: 2,
            margin: '4px 28px',
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.25)',
            fontSize: 13,
            color: '#fca5a5',
          }}
        >
          {error}
        </div>
      )}

      {/* conversation */}
      <div
        ref={messagesRef}
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          overflow: 'auto',
          padding: '16px 28px 10px',
        }}
      >
        <div
          style={{
            maxWidth: 640,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 13,
          }}
        >
          {/* privacy reassurance as the OPENING line of the thread */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                padding: '7px 14px',
                borderRadius: 999,
                background: `${cfg.deep}1a`,
                border: `1px solid ${cfg.deep}3a`,
                maxWidth: '100%',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z"
                  stroke={cfg.soft}
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ fontSize: 11.5, color: 'rgba(240,232,250,0.78)', lineHeight: 1.4 }}>
                <b style={{ color: '#fff', fontWeight: 700 }}>your room with f8.</b>{' '}
                private — {otherName} never sees what&apos;s said here unless you carry it over.
              </span>
            </div>
          </div>


          {!initialLoadComplete && (
            <div style={{ color: 'rgba(232,220,250,0.5)', padding: '8px 0' }}>loading…</div>
          )}

          {atLimit && initialLoadComplete && (
            <div
              role="note"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                fontSize: 12,
                color: 'rgba(232,220,250,0.65)',
                alignSelf: 'flex-start',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M21 8H3m18 0a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2m18 0v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
              <span>
                showing the last {MESSAGE_LIMIT} messages — earlier conversation has been archived
              </span>
            </div>
          )}

          {/* Fallback opening shown only while we wait for f8's real first
              message — once any message arrives, the real messages list takes
              over. A SINGLE bubble (f8's opener is one turn); the two framing
              lines render as stacked paragraphs, not two separate f8 turns
              (PPL-154). */}
          {isEmpty && side && (
            <F8Bubble kicker={side === 'invitee' ? 'just now' : 'in your corner'}>
              {fallbackOpening.map((line, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '0.6em 0 0' }}>
                  {line}
                </p>
              ))}
            </F8Bubble>
          )}

          {messages.map((m) =>
            isF8Message(m) ? (
              <F8Bubble key={m.id}>{m.text}</F8Bubble>
            ) : (
              <UserBubble key={m.id}>{m.text}</UserBubble>
            ),
          )}

          {isSending && (
            <F8Bubble kicker="thinking…">
              <span
                aria-hidden="true"
                style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#f0abfc',
                    opacity: 0.8,
                  }}
                />
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#f0abfc',
                    opacity: 0.55,
                  }}
                />
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: '#f0abfc',
                    opacity: 0.3,
                  }}
                />
              </span>
            </F8Bubble>
          )}

          {showChips && side && chips.length > 0 && (
            <div style={{ paddingLeft: 43, marginTop: 4 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'rgba(232,220,250,0.4)',
                  marginBottom: 9,
                }}
              >
                a way in — or just type below
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {chips.map((c) => (
                  <ReplyChip
                    key={c}
                    label={c}
                    arrowColor={cfg.soft}
                    onClick={() => void handleSend(c)}
                    disabled={isSending}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Excursion context — the last item in the message flow so it sits at
              the bottom (below the prep history) and scrolls with the chat, where
              the auto-scroll lands the user. Waiting + step-aside variants. */}
          {isExcursion && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '11px 15px',
                borderRadius: 14,
                background: `${cfg.deep}24`,
                border: `1px solid ${cfg.deep}45`,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
                {isAside ? (
                  <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z" stroke={cfg.soft} strokeWidth="1.7" strokeLinejoin="round" />
                ) : (
                  <>
                    <circle cx="12" cy="12" r="9" stroke={cfg.soft} strokeWidth="1.7" />
                    <path d="M12 7.5V12l3 2" stroke={cfg.soft} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </>
                )}
              </svg>
              <span style={{ fontSize: 12, color: 'rgba(240,232,250,0.82)', lineHeight: 1.45 }}>
                {isAside ? (
                  <>
                    <b style={{ color: '#fff', fontWeight: 700 }}>you&apos;ve stepped aside.</b>{' '}
                    this is a private moment with f8 — nothing you say here enters the room unless you bring it back yourself. tap “back to room” when you&apos;re ready.
                  </>
                ) : (
                  <>
                    <b style={{ color: '#fff', fontWeight: 700 }}>this is the waiting room.</b>{' '}
                    your rules are set — keep chatting with f8 while you wait for {otherName} to confirm. we&apos;ll pull you into the room the moment they do.
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* composer — slim, no separate advance row (advance lives in the top
          strip's "end prep" popover). */}
      <div style={{ position: 'relative', zIndex: 2, padding: '8px 28px 20px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div
            style={{
              padding: 1,
              borderRadius: 18,
              background: canSend
                ? `linear-gradient(135deg, ${cfg.deep}, #a855f7)`
                : 'rgba(255,255,255,0.08)',
            }}
          >
            <div style={{ borderRadius: 17, background: 'rgba(16,9,22,0.94)', padding: '13px 16px 11px' }}>
              <textarea
                ref={inputRef}
                value={inputText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
                maxLength={MAX_INPUT_LENGTH}
                disabled={isSending}
                placeholder="say what's actually going on…"
                style={{
                  width: '100%',
                  resize: 'none',
                  background: 'transparent',
                  border: 0,
                  outline: 'none',
                  color: '#fff',
                  fontFamily: 'inherit',
                  fontSize: 14.5,
                  lineHeight: 1.45,
                  padding: 0,
                  minHeight: 24,
                  maxHeight: 200,
                }}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 12,
                }}
              >
                <span style={{ fontSize: 11, color: 'rgba(232,220,250,0.4)' }}>
                  f8 may make mistakes · only you and f8 see this
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'rgba(232,220,250,0.4)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {inputText.length}/{MAX_INPUT_LENGTH}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleSend()}
                    disabled={!canSend}
                    title="send"
                    style={{
                      appearance: 'none',
                      border: 0,
                      cursor: canSend ? 'pointer' : 'not-allowed',
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: canSend ? '#fff' : 'rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1a0e22',
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M12 19V5m0 0l-6 6m6-6l6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomSoloPrep;
